import React, { useCallback, useEffect, useRef, useState } from 'react'
import services from '../../services'
import { useAuth } from '../../hooks/useAuth'
import Janela from '../componentes/Janela'
import CampoBusca from './CampoBusca'
import SeletorAcesso from './SeletorAcesso'
import {
  ArvoreAmbiente, ConferenciaCadastro, conferirArquivo, corpoDaAbertura, cpfObrigatorio, dashboardsDoAmbiente, Detalhe, faltaNoForm,
  filtrarOpcoes, FORM_VAZIO, FormNova, hojeISO, mascaraCpf, MAX_ANEXOS, Opcao, PRECISA_DE_ACESSO, PRIORIDADES, soDigitos,
  TIPOS, TIPOS_DE_ALTERACAO, tamanhoLegivel, VIAS
} from './comum'
import './solicitacoes.scss'

interface Props {
  aberto: boolean
  aoFechar: () => void
  /** Chamado com o pedido criado e os nomes dos anexos que não subiram (o pedido fica criado mesmo assim). */
  aoCriar: (criada: Detalhe, anexosComFalha: string[]) => void
}

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

/** Formulário de abertura: o tipo escolhido muda os campos. Os anexos sobem depois que o pedido é criado. */
export default function NovaSolicitacao({ aberto, aoFechar, aoCriar }: Props) {
  const { getUser } = useAuth()
  const euId: number | undefined = getUser()?.id
  const [f, setF] = useState<FormNova>(FORM_VAZIO)
  const [arquivos, setArquivos] = useState<File[]>([])
  const [arvore, setArvore] = useState<ArvoreAmbiente[] | null>(null)
  const [dominiosComCpf, setDominiosComCpf] = useState<string[]>([])
  const [conferencia, setConferencia] = useState<ConferenciaCadastro | null>(null)
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const seletor = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!aberto) return
    setF(FORM_VAZIO); setArquivos([]); setErro(null); setSalvando(false); setConferencia(null); setArvore(null)
    services.api.solicitacoesArvore().then((r: any) => setArvore(r.fail ? [] : r.data || []))
    services.api.solicitacoesConfig().then((r: any) => { if (!r.fail) setDominiosComCpf(r.data?.dominiosComCpf || []) })
  }, [aberto])

  const set = <K extends keyof FormNova>(chave: K, valor: FormNova[K]) => setF(x => ({ ...x, [chave]: valor }))
  const texto = (chave: keyof FormNova) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    set(chave, e.target.value as never)

  const precisaCpf = f.tipo === 'cadastro_usuario' && cpfObrigatorio(f.email, dominiosComCpf)
  const cpfDigitos = soDigitos(f.cpf)

  // Enquanto digita, o servidor diz se o e-mail ou o CPF já são de alguém.
  useEffect(() => {
    if (!aberto || f.tipo !== 'cadastro_usuario') return
    const email = f.email.trim()
    if (!EMAIL.test(email) && cpfDigitos.length !== 11) { setConferencia(null); return }
    let vivo = true
    const t = setTimeout(async () => {
      const r = await services.api.solicitacoesConferirCadastro(EMAIL.test(email) ? email : '', cpfDigitos.length === 11 ? cpfDigitos : '')
      if (vivo && !r.fail) setConferencia(r.data)
    }, 400)
    return () => { vivo = false; clearTimeout(t) }
  }, [aberto, f.tipo, f.email, cpfDigitos])

  const buscarPessoas = useCallback(async (q: string) => { const r = await services.api.solicitacoesBuscar('pessoas', q); return r.fail ? [] : r.data as Opcao[] }, [])
  const buscarEquipes = useCallback(async (q: string) => { const r = await services.api.solicitacoesBuscar('equipes', q); return r.fail ? [] : r.data as Opcao[] }, [])
  // Só aparece quem pode abrir solicitações (o servidor filtra pela permissão): os outros não conseguiriam abrir a tela.
  const buscarAcompanhantes = useCallback(async (q: string) => {
    const r = await services.api.solicitacoesBuscar('acompanhantes', q)
    return r.fail ? [] : (r.data as Opcao[]).filter(p => p.id !== euId)
  }, [euId])
  const buscarReferencia = useCallback(async (q: string) => (await buscarPessoas(q)).filter(p => p.id !== f.pessoa?.id), [buscarPessoas, f.pessoa])

  const falta = faltaNoForm(f, { hoje: hojeISO(), dominiosComCpf, conferencia })
  const semAcessoNenhum = !!f.tipo && PRECISA_DE_ACESSO.includes(f.tipo) && arvore !== null && arvore.length === 0

  const ambiente = arvore?.find(a => a.id === f.cliente?.id)
  // Todos levam o grupo no detalhe: a pessoa acha o dashboard pelo nome ou pela área.
  const dashboards = dashboardsDoAmbiente(ambiente, true)
  const opcoesAmbiente: Opcao[] = (arvore || []).map(a => ({ id: a.id, nome: a.nome }))
  const buscarAmbiente = async (q: string) => filtrarOpcoes(opcoesAmbiente, q)
  const buscarDashboard = async (q: string) => filtrarOpcoes(dashboards, q)
  // Trocar de ambiente limpa o dashboard; escolher o mesmo de novo não mexe nele.
  const escolherAmbiente = (v: Opcao[]) => {
    const a = arvore?.find(x => x.id === v[0]?.id)
    setF(x => ({ ...x, cliente: a ? { id: a.id, nome: a.nome } : null, dashboard: x.cliente?.id === a?.id ? x.dashboard : null }))
  }

  const escolherArquivos = (e: React.ChangeEvent<HTMLInputElement>) => {
    const novos = Array.from(e.target.files || [])
    e.target.value = ''
    const aceitos = [...arquivos]
    for (const a of novos) {
      const problema = conferirArquivo(a.name, a.size)
      if (problema) { setErro(`${a.name}: ${problema}`); continue }
      if (aceitos.length >= MAX_ANEXOS) { setErro(`Cada solicitação aceita até ${MAX_ANEXOS} anexos.`); break }
      aceitos.push(a)
    }
    setArquivos(aceitos)
  }

  const enviar = async () => {
    if (falta) return
    setSalvando(true); setErro(null)
    const r = await services.api.solicitacoesCriar(corpoDaAbertura(f))
    if (r.fail) { setErro(r.error || 'Não foi possível abrir a solicitação.'); setSalvando(false); return }
    const criada: Detalhe = r.data
    const falhas: string[] = []
    for (const a of arquivos) {
      const up = await services.api.solicitacaoEnviarAnexo(criada.id, a)
      if (up.fail) falhas.push(a.name)
    }
    setSalvando(false)
    aoCriar(criada, falhas)
  }

  const trocarTipo = (tipo: string) => setF({
    ...FORM_VAZIO, tipo: tipo as FormNova['tipo'], titulo: f.titulo, descricao: f.descricao, prioridade: f.prioridade, prazo: f.prazo,
    acompanhantes: f.acompanhantes
  })

  const referencia = (rotulo: string) => (
    <CampoBusca rotulo={rotulo} dica="Buscar por nome ou e-mail" buscar={buscarReferencia} valor={f.espelho ? [f.espelho] : []}
                aoMudar={v => set('espelho', v[0] || null)} unico />
  )

  // Cadastro e pedido de acesso perguntam primeiro como orientar o acesso; só o campo da opção escolhida aparece.
  const caminhoDoAcesso = (opcional: boolean) => (<>
    <div className="ng-campo">
      <span>Como orientar o acesso{opcional ? ' (opcional)' : ''}</span>
      <div className="sol-vias" role="radiogroup" aria-label="Como orientar o acesso">
        {opcional && (
          <button type="button" role="radio" aria-checked={f.via === ''} className={`sol-via${f.via === '' ? ' on' : ''}`} onClick={() => set('via', '')}>Nenhum</button>
        )}
        {VIAS.map(v => (
          <button key={v.id} type="button" role="radio" aria-checked={f.via === v.id} className={`sol-via${f.via === v.id ? ' on' : ''}`} onClick={() => set('via', v.id)}>{v.rotulo}</button>
        ))}
      </div>
    </div>
    {f.via === 'dashboards' && (
      <SeletorAcesso rotulo="Dashboards" arvore={arvore || []} carregando={arvore === null} valor={f.acessos} aoMudar={v => set('acessos', v)} />
    )}
    {f.via === 'equipes' && (
      <CampoBusca rotulo="Equipes" dica="Buscar equipe" buscar={buscarEquipes} valor={f.equipes} aoMudar={v => set('equipes', v)} />
    )}
    {f.via === 'espelho' && (<>
      {referencia('Pessoa de referência')}
      <small className="sol-ajuda">O time de BI libera o mesmo acesso que essa pessoa tem hoje.</small>
    </>)}
  </>)

  const rotuloAmbiente = f.tipo === 'dashboard_novo' ? 'Cliente' : 'Ambiente'
  const seletorAmbiente = (
    <CampoBusca rotulo={rotuloAmbiente} dica={arvore === null ? 'Carregando…' : 'Buscar ou escolher'} buscar={buscarAmbiente} minimo={0} unico
                valor={f.cliente ? [f.cliente] : []} aoMudar={escolherAmbiente} desabilitado={!arvore?.length} />
  )

  return (
    <Janela aberto={aberto} titulo="Nova solicitação" sub="O time de BI faz a triagem e você acompanha por aqui." botao="Abrir solicitação"
            desabilitado={!!falta} salvando={salvando} erro={erro} aoConfirmar={enviar} aoFechar={aoFechar}>
      <label className="ng-campo">
        <span>O que você precisa?</span>
        <select value={f.tipo} onChange={e => trocarTipo(e.target.value)} aria-label="Tipo de solicitação">
          <option value="">Escolha…</option>
          {TIPOS.map(t => <option key={t.id} value={t.id}>{t.rotulo}</option>)}
        </select>
        {f.tipo && <small className="sol-ajuda">{TIPOS.find(t => t.id === f.tipo)?.ajuda}</small>}
      </label>

      {semAcessoNenhum && (
        <small className="sol-aviso">Você ainda não acessa nenhum ambiente, então não dá para abrir este tipo de solicitação.</small>
      )}

      {f.tipo === 'cadastro_usuario' && (<>
        <label className="ng-campo"><span>Nome da pessoa</span><input value={f.nome} onChange={texto('nome')} maxLength={150} /></label>
        <label className="ng-campo"><span>E-mail</span>
          <input type="email" value={f.email} onChange={texto('email')} maxLength={200} />
          {conferencia?.emailJaExiste && <small className="sol-erro-campo">Já existe uma pessoa com esse e-mail no portal.</small>}
        </label>
        {precisaCpf && (
          <label className="ng-campo"><span>CPF</span>
            <input value={f.cpf} onChange={e => set('cpf', mascaraCpf(e.target.value))} inputMode="numeric" placeholder="000.000.000-00" maxLength={14} />
            <small className="sol-ajuda">Os e-mails deste domínio exigem o CPF no cadastro.</small>
            {cpfDigitos.length === 11 && conferencia?.cpfInvalido && <small className="sol-erro-campo">Informe um CPF válido.</small>}
            {cpfDigitos.length === 11 && conferencia?.cpfJaExiste && <small className="sol-erro-campo">Esse CPF já consta em outra pessoa do portal.</small>}
          </label>
        )}
        {caminhoDoAcesso(true)}
      </>)}

      {f.tipo === 'acesso_usuario' && (<>
        <CampoBusca rotulo="Pessoa que vai receber o acesso" dica="Buscar por nome ou e-mail" buscar={buscarPessoas} valor={f.pessoa ? [f.pessoa] : []}
                    aoMudar={v => setF(x => ({ ...x, pessoa: v[0] || null, espelho: x.espelho && v[0] && x.espelho.id === v[0].id ? null : x.espelho }))} unico />
        {caminhoDoAcesso(false)}
      </>)}

      {f.tipo === 'dashboard_novo' && (<>
        {seletorAmbiente}
      </>)}

      {f.tipo === 'dashboard_alteracao' && (<>
        {seletorAmbiente}
        <CampoBusca rotulo="Dashboard" dica={f.cliente ? 'Buscar por nome ou grupo' : 'Escolha o ambiente primeiro'} buscar={buscarDashboard} minimo={0} unico
                    valor={f.dashboard ? [f.dashboard] : []} desabilitado={!f.cliente}
                    aoMudar={v => set('dashboard', v[0] ? { id: v[0].id, nome: v[0].nome } : null)} />
        <label className="ng-campo"><span>Tipo da alteração (opcional)</span>
          <select value={f.tipoAlteracao} onChange={texto('tipoAlteracao')}>{TIPOS_DE_ALTERACAO.map(t => <option key={t.id} value={t.id}>{t.rotulo}</option>)}</select>
        </label>
      </>)}

      {f.tipo && (<>
        <label className="ng-campo"><span>Título</span><input value={f.titulo} onChange={texto('titulo')} maxLength={150} placeholder="Em poucas palavras" /></label>
        <label className="ng-campo"><span>Descrição</span><textarea rows={4} value={f.descricao} onChange={texto('descricao')} maxLength={5000} placeholder="Conte o que você precisa e para quê" /></label>
        <div className="sol-duas">
          <label className="ng-campo"><span>Prioridade</span>
            <select value={f.prioridade} onChange={texto('prioridade')}>{PRIORIDADES.map(p => <option key={p.id} value={p.id}>{p.rotulo}</option>)}</select>
          </label>
          <label className="ng-campo"><span>Prazo desejado (opcional)</span><input type="date" min={hojeISO()} value={f.prazo} onChange={texto('prazo')} /></label>
        </div>

        <CampoBusca rotulo="Compartilhar com (opcional)" dica="Buscar por nome ou e-mail" buscar={buscarAcompanhantes} valor={f.acompanhantes}
                    aoMudar={v => set('acompanhantes', v)} />
        <small className="sol-ajuda">Quem você incluir vê a solicitação, conversa e anexa junto com você, e recebe os avisos no sino.</small>

        <div className="ng-campo">
          <span>Anexos (opcional, até {MAX_ANEXOS} de 4 MB)</span>
          <div>
            <button type="button" className="ng-btn" onClick={() => seletor.current?.click()} disabled={arquivos.length >= MAX_ANEXOS}>Escolher arquivos</button>
            <input ref={seletor} type="file" multiple hidden onChange={escolherArquivos} aria-label="Escolher arquivos" />
          </div>
          {arquivos.length > 0 && (
            <ul className="sol-arquivos">
              {arquivos.map((a, i) => (
                <li key={`${a.name}-${i}`}>{a.name} <small>{tamanhoLegivel(a.size)}</small>
                  <button type="button" onClick={() => setArquivos(arquivos.filter((_, j) => j !== i))} aria-label={`Tirar ${a.name}`}>tirar</button>
                </li>
              ))}
            </ul>
          )}
        </div>
        {falta && <small className="sol-falta">{falta}</small>}
      </>)}
    </Janela>
  )
}
